import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  MutableRefObject
} from "react";
import Image from "next/image";
import { X } from "lucide-react";

export interface VideoPlayerProps {
  videoUrl: string;
  controls?: boolean;
  autoPlay?: boolean;
  muted?: boolean;
  fullScreen?: boolean;
  thumbnailUrl?: string;
  onClose?: (analytics: { percentWatched: number }) => void;
  onPlay?: () => void;
  onInitialPlay?: () => void;
  onPause?: () => void;
  onSeek?: (time: number) => void;
  onEnded?: () => void;
  showCloseButton?: boolean;
  playInViewport?: boolean; // Controls if video should autoplay when visible in viewport
}

/**
 * VideoPlayer - A flexible video player component with advanced features.
 *
 * This component supports:
 * - Standard video controls (play, pause, seeking)
 * - Thumbnail display with play button overlay
 * - Fullscreen capability
 * - Play-in-viewport functionality
 * - Multiple callback events for analytics
 * - RefForwarding to allow parent components to access the video element
 *
 * Usage examples:
 * 1. Basic usage: <VideoPlayer videoUrl="/path/video.mp4" />
 * 2. With thumbnail: <VideoPlayer videoUrl="/video.mp4" thumbnailUrl="/thumbnail.jpg" />
 * 3. With ref: <VideoPlayer ref={videoRef} videoUrl="/video.mp4" />
 * 4. With viewport detection: <VideoPlayer videoUrl="/video.mp4" playInViewport={true} />
 */
const VideoPlayer = forwardRef<HTMLVideoElement, VideoPlayerProps>(
  (
    {
      videoUrl,
      controls = true,
      autoPlay = false,
      muted = false,
      fullScreen = false,
      thumbnailUrl,
      onClose,
      onPlay,
      onInitialPlay,
      onPause,
      onSeek,
      onEnded,
      showCloseButton = false,
      playInViewport = false
    },
    forwardedRef
  ) => {
    const [isPlaying, setIsPlaying] = useState(autoPlay);
    const [percentWatched, setPercentWatched] = useState(0);
    const [showThumbnail, setShowThumbnail] = useState(!!thumbnailUrl);
    const [hasPlayedOnce, setHasPlayedOnce] = useState(false);
    const [isInViewport, setIsInViewport] = useState(false);

    // Internal ref used as fallback when forwardedRef isn't available or is a function ref
    const internalVideoRef = useRef<HTMLVideoElement>(null);
    // Container ref used for viewport intersection detection
    const containerRef = useRef<HTMLDivElement>(null);

    /**
     * Helper function to get the video DOM element
     * This handles both types of refs (callback and object) and provides a consistent
     * way to access the video element throughout the component
     */
    const getVideoElement = (): HTMLVideoElement | null => {
      // If forwardedRef is a callback ref, we can't read it, so use internal ref
      if (typeof forwardedRef === "function") {
        return internalVideoRef.current;
      }
      // If forwardedRef is an object ref, use it if it has a value, otherwise use internal ref
      return (
        (forwardedRef as MutableRefObject<HTMLVideoElement | null>)?.current ||
        internalVideoRef.current
      );
    };

    // Intersection Observer setup to detect when video is in viewport
    useEffect(() => {
      // Skip if container isn't available or feature isn't enabled
      if (!containerRef.current || !playInViewport) return;

      // Create an observer that triggers when video enters/exits viewport
      const observer = new IntersectionObserver(
        entries => {
          const [entry] = entries;
          setIsInViewport(entry.isIntersecting);
        },
        { threshold: 0.7 } // Consider the video in viewport when 70% visible
      );

      observer.observe(containerRef.current);

      // Clean up observer on component unmount
      return () => {
        if (containerRef.current) {
          observer.unobserve(containerRef.current);
        }
      };
    }, [playInViewport]);

    // Control video playback based on viewport visibility
    useEffect(() => {
      // Skip if playInViewport feature isn't enabled
      if (!playInViewport) return;

      const video = getVideoElement();
      if (!video) return;

      // Automatically play when in viewport
      if (isInViewport) {
        if (video.paused) {
          video.play().catch(err => {
            console.error("Error playing video when in viewport:", err);
          });
        }
      } else {
        // Automatically pause when out of viewport
        if (!video.paused) {
          video.pause();
        }
      }
    }, [isInViewport, playInViewport]);

    // Set up video event handling and fullscreen functionality
    useEffect(() => {
      const video = getVideoElement();
      if (!video) return;

      // Handle fullscreen request if enabled
      if (fullScreen) {
        video.requestFullscreen().catch(err => {
          console.error(
            `Error attempting to enable fullscreen: ${err.message}`
          );
        });
      }

      // Track video progress to calculate percentWatched
      const handleTimeUpdate = () => {
        if (!video.duration) return;
        const percent = Math.floor((video.currentTime / video.duration) * 100);
        setPercentWatched(percent);
      };

      // Handle play events, including first play detection
      const handlePlay = () => {
        setIsPlaying(true);
        onPlay?.();

        // Track first play for analytics
        if (!hasPlayedOnce) {
          setHasPlayedOnce(true);
          onInitialPlay?.();
        }

        // Hide thumbnail when video starts playing
        setShowThumbnail(false);
      };

      const handlePause = () => {
        setIsPlaying(false);
        onPause?.();
      };

      const handleSeek = () => {
        onSeek?.(video.currentTime);
      };

      const handleEnded = () => {
        setIsPlaying(false);
        onEnded?.();
      };

      // Set up all event listeners
      video.addEventListener("timeupdate", handleTimeUpdate);
      video.addEventListener("play", handlePlay);
      video.addEventListener("pause", handlePause);
      video.addEventListener("seeked", handleSeek);
      video.addEventListener("ended", handleEnded);

      // Clean up event listeners on component unmount or ref change
      return () => {
        video.removeEventListener("timeupdate", handleTimeUpdate);
        video.removeEventListener("play", handlePlay);
        video.removeEventListener("pause", handlePause);
        video.removeEventListener("seeked", handleSeek);
        video.removeEventListener("ended", handleEnded);
      };
    }, [
      onPlay,
      onPause,
      onSeek,
      onEnded,
      fullScreen,
      hasPlayedOnce,
      onInitialPlay,
      forwardedRef // Dependency on forwardedRef to re-setup listeners if ref changes
    ]);

    /**
     * Toggle play/pause state of the video
     * Used by the thumbnail overlay play button
     */
    const togglePlayPause = () => {
      const video = getVideoElement();
      if (!video) return;

      if (video.paused) {
        video.play().catch(err => {
          console.error("Error playing video:", err);
        });
      } else {
        video.pause();
      }
    };

    /**
     * Handle close button click
     * Reports the percentage of video watched via onClose callback
     */
    const handleClose = (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent event bubbling

      if (onClose) {
        onClose({ percentWatched });
      }
    };

    return (
      <div
        ref={containerRef}
        className="relative w-full h-full"
        role="application"
        aria-label="Video Player"
      >
        {/* Close button - only shown when showCloseButton is true */}
        {showCloseButton ? (
          <button
            onClick={handleClose}
            className="absolute top-2 right-2 z-1 bg-black bg-opacity-50 text-white p-2 rounded-full"
            aria-label="Close video"
          >
            <X size={16} />
          </button>
        ) : null}

        {/* Thumbnail with play button overlay - shown before video plays */}
        {showThumbnail && thumbnailUrl && (
          <div className="absolute inset-0 z-1">
            <div className="relative w-full h-full">
              <Image
                src={thumbnailUrl}
                alt="Video thumbnail"
                layout="fill"
                objectFit="cover"
              />
              {/* Play button overlay */}
              <div className="absolute inset-0 flex items-center justify-center">
                <Image
                  src="/hire/video-play.png"
                  alt="Play video"
                  width={80}
                  height={80}
                  onClick={togglePlayPause}
                  className="cursor-pointer"
                />
              </div>
            </div>
          </div>
        )}

        {/* Video element */}
        <div className="w-full h-full">
          <video
            ref={node => {
              // This ref callback handles both function and object refs
              if (typeof forwardedRef === "function") {
                // For function refs: call the function with the node
                forwardedRef(node);
              } else if (forwardedRef) {
                // For object refs: set the .current property
                (
                  forwardedRef as React.MutableRefObject<HTMLVideoElement>
                ).current = node;
              }
              // Always update internal ref for component's internal use
              internalVideoRef.current = node;
            }}
            className="w-full h-full"
            src={videoUrl}
            controls={controls}
            // Handle autoplay based on viewport visibility if feature is enabled
            autoPlay={playInViewport ? isInViewport && autoPlay : autoPlay}
            muted={muted}
            controlsList="nodownload"
            disablePictureInPicture
          >
            <source src={videoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      </div>
    );
  }
);

VideoPlayer.displayName = "VideoPlayer";

export default VideoPlayer;
